// create-ai-slut analytics helpers.
//
// All analytics flows through GTM (window.dataLayer.push) — this lander never
// talks to Mixpanel directly (no SDK / token). The shared GTM container holds
// the Mixpanel token; every event below is GTM/GA-only. `lp_source` lets the
// shared container tell create_ai_slut traffic apart from the other landers.

import { LP_SOURCE } from './api';

declare global {
  interface Window {
    dataLayer: unknown[];
  }
}

// Default until GrowthBook resolves. Matches the `name: "default"` value
// the team uses for the baked-in `create-ai-slut-content` payload.
const DEFAULT_LANDER_CONTENT = 'default';

// `lander-content` — identifies which GrowthBook content-variant is live, taken
// from the config's top-level `name` field (e.g. "default"). Sent on EVERY
// event alongside `lp_source`. Defaults to 'default' (the baked-in config)
// until GrowthBook resolves and useGrowthbook calls setLanderContent().
let landerContent = DEFAULT_LANDER_CONTENT;

// Seeded into the GTM dataLayer at TagManager.initialize — every event the
// container fires inherits both keys from then on.
export const INITIAL_DATA_LAYER = {
  lp_source: LP_SOURCE,
  'lander-content': DEFAULT_LANDER_CONTENT,
};

// Called by useGrowthbook once the GB payload resolves, passing the flag's
// top-level `name`. No-ops on empty/missing names so events keep the default.
// Updates both the module-local value (used by the explicit events below) and
// the dataLayer (so container auto-events pick up the latest value too).
export function setLanderContent(name: string | undefined | null): void {
  if (!name) return;
  landerContent = name;
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ 'lander-content': name });
}

function pushGtm(event: string, props: Record<string, unknown> = {}): void {
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer || [];
  // Every event carries `lp_source` + `lander-content`. Spread caller props
  // last so an explicit override still wins.
  window.dataLayer.push({
    event,
    lp_source: LP_SOURCE,
    'lander-content': landerContent,
    ...props,
  });
}

// Fires on every "Create My Slut" CTA button (hero + bottom banners).
export function trackCtaClicked(): void {
  pushGtm('cta_clicked');
}

// Fires when the registration form (AuthModal) opens, from any entry point.
export function trackRegistrationPopupOpened(): void {
  pushGtm('lander_registration_popup');
}

// Fires when a girl card is clicked, carrying the girl's name in the dataLayer.
export function trackGirlClick(name: string): void {
  pushGtm('girl_click', { girl_name: name });
}

// Registration succeeded. Carries the registered user's email in the dataLayer.
// GTM-only.
export function trackLanderRegistrationCompleted(email?: string): void {
  pushGtm('lander_registration_completed', email ? { user_email: email } : {});
}

// Fires on successful email/password login. GTM-only.
export function trackLoginCompleted(): void {
  pushGtm('lander_login_completed');
}

// Fires after a password reset successfully completes (new password set).
// GTM-only.
export function trackForgotPasswordCompleted(): void {
  pushGtm('lander_forgot_pass_completed');
}
